import { useCallback } from "react";

const useRecordServiceMethods = (api) => {
  const searchRecords = useCallback(
    async (payload, currentPage = 0, pageSize = 10, sortBy = "created_at:desc", signal = null) => {
      try {
        var searchPayload = payload;
        searchPayload.moduleName = payload.moduleName;
        searchPayload.onlyDefault = payload.onlyDefault || false;

        const requestConfig = {
          params: {
            page: currentPage,
            size: pageSize,
            sortBy: sortBy,
          },
        };

        if (signal) {
          requestConfig.signal = signal;
        }

        const response = await api.post("/record/search", searchPayload, requestConfig);
        return {
          data: {
            content: response.data.content,
            totalElements: response.data.totalElements,
            totalPages: response.data.totalPages,
            size: response.data.size,
            number: response.data.number,
            first: response.data.first,
            last: response.data.last,
          },
          status: response.status,
        };
      } catch (error) {
        if (error.code === "ERR_CANCELED") {} else {
          console.error("Error searching records:", error);
          throw error;
        }
      }
    },
    [api]
  );

  const createEntry = useCallback(
    async (payload) => {
      try {
        const createRequest = {
          moduleName: payload.moduleName,
          columns: payload.columns,
        };
        const response = await api.post("/record", createRequest);
        return response;
      } catch (error) {
        console.error("Error creating record:", error);
        throw error;
      }
    },
    [api]
  );

  const deleteRecord = useCallback(
    async (recordId) => {
      try {
        await api.delete(`/record/${recordId}`);
        return {
          data: {
            success: true,
            message: "Record deleted successfully",
          },
          status: 200,
        };
      } catch (error) {
        console.error("Error deleting record:", error);
        throw error;
      }
    },
    [api]
  );

  const deleteMultipleRecords = useCallback(
    async (recordIds) => {
      try {
        const response = await api.delete("/record/bulk", {
          data: {
            recordIds,
          },
        });
        return response;
      } catch (error) {
        console.error("Error deleting multiple records:", error);
        throw error;
      }
    },
    [api]
  );

  const getRecordDetails = useCallback(
    async (recordId) => {
      try {
        const response = await api.get(`/record/${recordId}`);
        return {
          data: response.data,
          status: response.status,
        };
      } catch (error) {
        console.error("Error fetching record details:", error);
        throw error;
      }
    },
    [api]
  );

  const updateRecordEntry = useCallback(
    async (recordEntryId, payload) => {
      try {
        const response = await api.put(`/record/${recordEntryId}`, payload);
        return response;
      } catch (error) {
        console.error("Error updating record entry:", error);
        throw error;
      }
    },
    [api]
  );

  const hasAnyRecords = useCallback(async () => {
    const response = await api.get("/record/exists");
    return response.data;
  }, [api]);

  const convertSearchPayload = useCallback((payload) => {
    const searchConditions = {};
    if (payload.allConditions && payload.allConditions.length > 0) {
      payload.allConditions.forEach((condition) => {
        searchConditions[condition.field] = {
          operator: condition.operator,
          value: condition.value,
        };
      });
    }
    if (payload.anyConditions && payload.anyConditions.length > 0) {
      payload.anyConditions.forEach((condition) => {
        searchConditions[`or_${condition.field}`] = {
          operator: condition.operator,
          value: condition.value,
        };
      });
    }
    return searchConditions;
  }, []);

  return {
    searchRecords,
    createEntry,
    deleteRecord,
    deleteMultipleRecords,
    getRecordDetails,
    updateRecordEntry,
    hasAnyRecords,
    convertSearchPayload,
  };
};

export default useRecordServiceMethods;
